import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { CreditsResponse } from '@shared/api';
import { getCredits } from '../../api/client';
import { creditsLine } from '../../lib/credits';
import { navigate } from '../../state/router';

/**
 * Proposals left (docs/limits.md), always visible at the foot of the sidebar
 * above the account button. Hidden when the rep has no limit or the fetch
 * fails — the server still refuses a build the rep has no credit for.
 *
 * `refreshKey` changes whenever a credit may have been spent (the active
 * proposal, its build status, the active org), which refetches.
 */
export function CreditsMeter({ refreshKey }: { refreshKey: string }): ReactElement | null {
  const [credits, setCredits] = useState<CreditsResponse | null>(null);

  useEffect(() => {
    let live = true;
    getCredits()
      .then((next) => {
        if (live) setCredits(next);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [refreshKey]);

  const line = creditsLine(credits);
  if (!credits || !line || credits.remaining === null) return null;
  // The bar tracks the limit that is closest to running out.
  const tightest = credits.limits.find((limit) => limit.remaining === credits.remaining);
  const fraction = tightest && tightest.credits > 0 ? Math.min(1, tightest.used / tightest.credits) : 1;

  return (
    <button
      type="button"
      className="sidebar-credits"
      data-blocked={line.blocked ? '' : undefined}
      title={line.text}
      onClick={() => navigate('/settings')}
    >
      <span className="sidebar-credits-text">
        {line.blocked ? 'No proposals left' : line.text}
      </span>
      <span className="sidebar-credits-bar" aria-hidden="true">
        <span style={{ width: `${Math.round((1 - fraction) * 100)}%` }} />
      </span>
    </button>
  );
}
